import axios from "axios"
import { deleteDuplicates } from "../../utils/deleteDuplicates"
import { Trip_legacy } from "../trips"
import { PlainDate } from "@/utils/date"

/**
 * Gets an array of dates between two dates that are considered working days
 * @param dateRange - Tuple of [startDate, endDate] as PlainDate objects
 * @param workingDays - Array of 3-letter weekday codes (e.g., ["Mon", "Tue"]) that are considered working days
 * @param holidays - Array of PlainDate objects representing holidays to exclude
 * @returns Array of PlainDate objects representing working days in the range, excluding holidays and non-working weekdays
 */
export const getWorkingDates = (
	dateRange: [PlainDate, PlainDate],
	workingDays: string[],
	holidays: PlainDate[]
): PlainDate[] => {
	const result: PlainDate[] = []
	const [start, end] = dateRange

	const days = start.toTemporal().until(end.toTemporal(), {
		largestUnit: "days",
	}).days

	for (let i = 0; i <= days; i++) {
		const currentDate = PlainDate.from(start.toTemporal().add({ days: i }).toString())

		const isHoliday = holidays.some((h) => PlainDate.compare(h, currentDate) === 0)
		const isWorkingDay = workingDays.includes(currentDate.formatShortWeekday())
		if (!isHoliday && isWorkingDay) {
			result.push(currentDate)
		}
	}

	return result
}

export function dateRangeToDays(dateRange: [string, string]): number {
	let start = new Date(dateRange[0])
	let end = new Date(dateRange[1])
	let diff = end.getTime() - start.getTime()
	if (diff < 0) return 0

	return 1 + Math.ceil(diff / (1000 * 3600 * 24))
}

type NagerHolidayResponse = {
	date: string
	localName: string
	types: string[]
}[]

type FetchHolidayReturn = {
	date: PlainDate
	localName: string
	types: string[]
}[]

export async function getUSHolidaysInRange(
	startDate: PlainDate,
	endDate: PlainDate
): Promise<FetchHolidayReturn> {
	const startYear = startDate.year()
	const endYear = endDate.year()
	const holidays: FetchHolidayReturn = []

	for (let year = startYear; year <= endYear; year++) {
		try {
			const { data } = await axios.get<NagerHolidayResponse>(
				`https://date.nager.at/api/v3/publicholidays/${year}/US`
			)

			// Filter holidays that fall within our date range
			const yearHolidays = data.map((holiday) => ({
				date: PlainDate.from(holiday.date),
				localName: holiday.localName,
				types: holiday.types,
			}))

			holidays.push(
				...yearHolidays.filter((holiday) =>
					PlainDate.isBetween(holiday.date, startDate, endDate)
				)
			)
		} catch (error) {
			console.error(`Failed to fetch holidays for ${year}:`, error)
		}
	}

	return deleteDuplicates(holidays, (a, b) => PlainDate.compare(a.date, b.date) === 0)
}

export const milesToMeters = (miles: number) => miles * 1609.3445

/**
 * @param dailyTimeLimit - Maximum time allowed for each day's trips
 * Calculated using working hours.
 * in:
 * 	- work start/end time
 * out:
 * 	- number (in minutes)
 */

/**
 * @param timeSpentInNodes - Array specifying time spent at each node
 * User input?
 * in:
 * 	- different time spent for node categories (office, supplier, customer, etc.)
 *  - which nodes belong to which category
 * out:
 * 	- number[]
 */

/**
 * Converts algorithmic trip data into UI-friendly trip format
 * @param tripDays - Array of trip indices for each day
 * @param distances - Matrix of distances between nodes where distances[i][j] is distance from node i to j
 * @param times - Matrix of travel times between nodes where times[i][j] is time to travel from node i to j
 * @param workingDates - Array of dates when trips occur
 * @param addresses - Array of address strings corresponding to node indices
 * @param timeSpentInNodes - Array of times spent at each node in minutes
 * @returns Array of formatted trip objects for UI display
 */
export const convertTrips = (
	tripDays: number[][],
	distances: number[][],
	times: number[][],
	workingDates: Date[],
	addresses: string[],
	timeSpentInNodes: number[],
	timeStartMin: number
): Trip_legacy[] => {
	return tripDays.reduce((acc, trips, day) => {
		const formattedTrips = _convertTrips(
			trips,
			day,
			distances,
			times,
			workingDates,
			addresses,
			timeSpentInNodes,
			timeStartMin
		)
		return [...acc, ...formattedTrips]
	}, [] as Trip_legacy[])
}

const _convertTrips = (
	trips: number[],
	day: number,
	distances: number[][],
	times: number[][],
	workingDates: Date[],
	addresses: string[],
	timeSpentInNodes: number[],
	timeStartMin: number
): Trip_legacy[] => {
	let result: Trip_legacy[] = []

	for (let i = 0; i < trips.length - 1; i++) {
		const date = workingDates[day]!

		const from = trips[i]!
		const to = trips[i + 1]!

		const distance = distances[from]![to]!

		const time = times[from]![to]! + timeSpentInNodes[from]!

		const mileage = distance / 1609.344 // Convert meters to miles

		result.push({
			id: i + date.getTime() + "",
			date: date.getTime(),
			startAddress: addresses[from]!,
			endAddress: addresses[to]!,
			mileage: Number(mileage.toFixed(2)),
			note: "",
			parking: 0,
			tolls: 0,
			vehicle: "Missing Vehicle",
			timeStart: addMinutes(date, timeStartMin).getTime(),
		})
		timeStartMin += time
	}
	return result
}

const addMinutes = (date: Date, minutes: number) => {
	return new Date(date.getTime() + minutes * 60000)
}
